import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { Memo, ChecklistItem } from '../../services/memoService';
import { Trash2, Check, X, GripVertical, Square, CheckSquare, Maximize2, Plus, Users, Share2, FileText, ListTodo, Palette, MoreVertical } from 'lucide-react';

interface MemoCardProps {
  memo: Memo;
  onUpdate: (id: number, updatedMemo: Partial<Memo>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onPositionChange: (id: number, positionX: number, positionY: number) => Promise<void>;
  onShare?: (memo: Memo) => void; // Share memo handler
  boardScale?: number; // Scale factor of the parent board
  viewMode?: 'grid' | 'list'; // Display mode
}

export default function MemoCard({ memo, onUpdate, onDelete, onPositionChange, onShare, boardScale = 1, viewMode = 'grid' }: MemoCardProps) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(memo.title);
  const [content, setContent] = useState(memo.content);
  const [selectedColor, setSelectedColor] = useState(memo.color);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedTitle, setExpandedTitle] = useState(memo.title);
  const [expandedContent, setExpandedContent] = useState(memo.content);
  const [expandedChecklistItems, setExpandedChecklistItems] = useState(memo.checklistItems || []);
  const [expandedColor, setExpandedColor] = useState(memo.color);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [draggedItemIndex, setDraggedItemIndex] = useState<number | null>(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const checklistContainerRef = useRef<HTMLDivElement>(null);
  const newItemInputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const autosaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Determine memo type with fallback for legacy memos without type field
  const memoType: 'note' | 'checklist' = memo.type || (memo.checklistItems && memo.checklistItems.length > 0 ? 'checklist' : 'note');

  const isOwner = !memo.accessLevel || memo.accessLevel === 'owner';
  const canEditMemo = isOwner || memo.accessLevel === 'edit';
  const isViewOnly = memo.accessLevel === 'view';

  // Helper function for autosaving checklist changes
  const autoSaveChecklistChanges = useCallback(async (items: ChecklistItem[]) => {
    if (!canEditMemo) return;

    try {
      await onUpdate(memo.id, {
        ...memo,
        title: expandedTitle,
        content: expandedContent,
        checklistItems: items,
        color: expandedColor
      });
    } catch (error) {
      console.error('Failed to autosave checklist changes:', error);
    }
  }, [memo, expandedTitle, expandedContent, expandedColor, onUpdate, canEditMemo]);

  // Debounced autosave for text changes (500ms delay)
  const debouncedAutoSave = useCallback((items: ChecklistItem[]) => {
    if (autosaveTimeoutRef.current) {
      clearTimeout(autosaveTimeoutRef.current);
    }

    autosaveTimeoutRef.current = setTimeout(() => {
      autoSaveChecklistChanges(items);
    }, 500);
  }, [autoSaveChecklistChanges]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autosaveTimeoutRef.current) {
        clearTimeout(autosaveTimeoutRef.current);
      }
    };
  }, []);

  // Auto-scroll when new checklist item is added
  useEffect(() => {
    if (expandedChecklistItems.length > 0 && checklistContainerRef.current) {
      checklistContainerRef.current.scrollTop = checklistContainerRef.current.scrollHeight;
    }
  }, [expandedChecklistItems.length]);

  // Handle ESC key to close expanded modal
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        handleCancelExpanded();
      }
      if (e.key === 'Escape' && isDeleting) {
        setIsDeleting(false);
      }
    };

    if (isExpanded || isDeleting) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isExpanded, isDeleting]);

  // Color styles for different memo colors
  const colorStyles: Record<string, { bg: string, shadow: string, darkBg: string, darkShadow: string }> = {
    yellow: {
      bg: 'bg-yellow-100',
      shadow: 'shadow-[5px_5px_10px_rgba(251,191,36,0.3)]',
      darkBg: 'bg-yellow-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(251,191,36,0.1)]'
    },
    blue: {
      bg: 'bg-blue-100',
      shadow: 'shadow-[5px_5px_10px_rgba(59,130,246,0.3)]',
      darkBg: 'bg-blue-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(59,130,246,0.1)]'
    },
    green: {
      bg: 'bg-green-100',
      shadow: 'shadow-[5px_5px_10px_rgba(34,197,94,0.3)]',
      darkBg: 'bg-green-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(34,197,94,0.1)]'
    },
    pink: {
      bg: 'bg-pink-100',
      shadow: 'shadow-[5px_5px_10px_rgba(236,72,153,0.3)]',
      darkBg: 'bg-pink-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(236,72,153,0.1)]'
    },
    purple: {
      bg: 'bg-purple-100',
      shadow: 'shadow-[5px_5px_10px_rgba(147,51,234,0.3)]',
      darkBg: 'bg-purple-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(147,51,234,0.1)]'
    },
    orange: {
      bg: 'bg-orange-100',
      shadow: 'shadow-[5px_5px_10px_rgba(249,115,22,0.3)]',
      darkBg: 'bg-orange-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(249,115,22,0.1)]'
    },
    red: {
      bg: 'bg-red-100',
      shadow: 'shadow-[5px_5px_10px_rgba(239,68,68,0.3)]',
      darkBg: 'bg-red-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(239,68,68,0.1)]'
    },
    teal: {
      bg: 'bg-teal-100',
      shadow: 'shadow-[5px_5px_10px_rgba(20,184,166,0.3)]',
      darkBg: 'bg-teal-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(20,184,166,0.1)]'
    },
    indigo: {
      bg: 'bg-indigo-100',
      shadow: 'shadow-[5px_5px_10px_rgba(99,102,241,0.3)]',
      darkBg: 'bg-indigo-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(99,102,241,0.1)]'
    },
    cyan: {
      bg: 'bg-cyan-100',
      shadow: 'shadow-[5px_5px_10px_rgba(6,182,212,0.3)]',
      darkBg: 'bg-cyan-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(6,182,212,0.1)]'
    },
    lime: {
      bg: 'bg-lime-100',
      shadow: 'shadow-[5px_5px_10px_rgba(132,204,22,0.3)]',
      darkBg: 'bg-lime-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(132,204,22,0.1)]'
    },
    rose: {
      bg: 'bg-rose-100',
      shadow: 'shadow-[5px_5px_10px_rgba(244,63,94,0.3)]',
      darkBg: 'bg-rose-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(244,63,94,0.1)]'
    },
    amber: {
      bg: 'bg-amber-100',
      shadow: 'shadow-[5px_5px_10px_rgba(245,158,11,0.3)]',
      darkBg: 'bg-amber-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(245,158,11,0.1)]'
    },
    emerald: {
      bg: 'bg-emerald-100',
      shadow: 'shadow-[5px_5px_10px_rgba(16,185,129,0.3)]',
      darkBg: 'bg-emerald-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(16,185,129,0.1)]'
    },
    violet: {
      bg: 'bg-violet-100',
      shadow: 'shadow-[5px_5px_10px_rgba(139,92,246,0.3)]',
      darkBg: 'bg-violet-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(139,92,246,0.1)]'
    },
    sky: {
      bg: 'bg-sky-100',
      shadow: 'shadow-[5px_5px_10px_rgba(14,165,233,0.3)]',
      darkBg: 'bg-sky-900',
      darkShadow: 'shadow-[5px_5px_10px_rgba(14,165,233,0.1)]'
    },
  };

  // Get color style based on memo color
  const getColorStyle = () => {
    const color = memo.color in colorStyles ? memo.color : 'yellow';
    return theme === 'dark'
      ? `${colorStyles[color].darkBg} ${colorStyles[color].darkShadow} text-gray-200`
      : `${colorStyles[color].bg} ${colorStyles[color].shadow} text-gray-800`;
  };

  // Available colors for sticky notes
  const availableColors = [
    'yellow', 'blue', 'green', 'pink', 'purple', 'orange',
    'red', 'teal', 'indigo', 'cyan', 'lime', 'rose',
    'amber', 'emerald', 'violet', 'sky'
  ];

  // Handle save changes
  const handleSave = useCallback(async () => {
    if (!canEditMemo) {
      return;
    }
    await onUpdate(memo.id, { title, content, color: selectedColor });
    setIsEditing(false);
  }, [memo.id, title, content, selectedColor, onUpdate, canEditMemo]);

  // Drag state to track if we're currently dragging
  const [isDragging, setIsDragging] = useState(false);

  // Handle toggling checklist item completion
  const handleToggleChecklistItem = useCallback(async (itemId: string) => {
    if (!canEditMemo || !memo.checklistItems) return;

    const updatedItems = memo.checklistItems.map(item =>
      item.id === itemId ? { ...item, completed: !item.completed } : item
    );

    try {
      await onUpdate(memo.id, {
        ...memo,
        checklistItems: updatedItems
      });
    } catch (error) {
      console.error('Failed to update checklist item:', error);
    }
  }, [memo.id, memo.checklistItems, onUpdate, canEditMemo]);

  const handleSaveExpanded = async () => {
    if (!canEditMemo) {
      return;
    }
    try {
      await onUpdate(memo.id, {
        ...memo,
        title: expandedTitle,
        content: expandedContent,
        checklistItems: expandedChecklistItems,
        color: expandedColor
      });
      setIsExpanded(false);
    } catch (error) {
      console.error('Failed to save memo:', error);
    }
  };

  const handleCancelExpanded = () => {
    // Reset to original values
    setExpandedTitle(memo.title);
    setExpandedContent(memo.content);
    setExpandedChecklistItems(memo.checklistItems || []);
    setExpandedColor(memo.color);
    setNewChecklistItem('');
    setIsExpanded(false);
  };

  // Convert between note and checklist types
  const handleConvertType = async (newType: 'note' | 'checklist') => {
    if (!canEditMemo) return;

    try {
      if (newType === 'checklist') {
        // Convert note to checklist - split content into checklist items
        let newChecklistItems: ChecklistItem[] = [];

        if (expandedContent && expandedContent.trim()) {
          // Split content by lines and create checklist items
          const lines = expandedContent
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0); // Remove empty lines

          if (lines.length > 0) {
            newChecklistItems = lines.map((line, index) => ({
              id: `temp-${Date.now()}-${index}`,
              text: line,
              completed: false
            }));
          }
        }

        // If no content or empty, create one empty item
        if (newChecklistItems.length === 0) {
          newChecklistItems = [{
            id: `temp-${Date.now()}`,
            text: '',
            completed: false
          }];
        }

        setExpandedChecklistItems(newChecklistItems);
        setExpandedContent(''); // Clear content when converting to checklist

        await onUpdate(memo.id, {
          ...memo,
          type: 'checklist',
          checklistItems: newChecklistItems,
          content: '', // Clear content field
          title: expandedTitle,
          color: expandedColor,
          positionX: memo.positionX,
          positionY: memo.positionY
        });
      } else {
        // Convert checklist to note - merge checklist items into content
        let newContent = '';

        if (expandedChecklistItems && expandedChecklistItems.length > 0) {
          // Convert checklist items to bullet points
          newContent = expandedChecklistItems
            .map(item => {
              const prefix = item.completed ? '✓' : '•';
              return `${prefix} ${item.text}`;
            })
            .join('\n');
        } else {
          // If checklist is empty, preserve existing content
          newContent = expandedContent || '';
        }

        setExpandedContent(newContent);
        setExpandedChecklistItems([]); // Clear checklist items

        await onUpdate(memo.id, {
          ...memo,
          type: 'note',
          checklistItems: [], // Clear checklist items
          content: newContent,
          title: expandedTitle,
          color: expandedColor,
          positionX: memo.positionX,
          positionY: memo.positionY
        });
      }
    } catch (error) {
      console.error('Failed to convert memo type:', error);
      // Revert state on error
      setExpandedChecklistItems(memo.checklistItems || []);
      setExpandedContent(memo.content);
    }
  };

  // Add new checklist item
  const handleAddChecklistItem = () => {
    if (!canEditMemo) {
      return;
    }
    if (newChecklistItem.trim()) {
      const newItem: ChecklistItem = {
        id: `temp-${Date.now()}`,
        text: newChecklistItem.trim(),
        completed: false
      };
      const updatedItems = [...expandedChecklistItems, newItem];
      setExpandedChecklistItems(updatedItems);
      setNewChecklistItem('');
      // Autosave the new item
      autoSaveChecklistChanges(updatedItems);
      // Focus back on input
      setTimeout(() => newItemInputRef.current?.focus(), 100);
    }
  };

  // Delete checklist item
  const handleDeleteChecklistItem = (itemId: string) => {
    if (!canEditMemo) {
      return;
    }
    const updatedItems = expandedChecklistItems.filter(item => item.id !== itemId);
    setExpandedChecklistItems(updatedItems);
    // Autosave the deletion
    autoSaveChecklistChanges(updatedItems);
  };

  // Handle Enter key in new item input
  const handleNewItemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!canEditMemo) {
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddChecklistItem();
    }
  };

  // Drag and drop handlers for checklist items
  const handleItemDragStart = (e: React.DragEvent, index: number) => {
    if (!canEditMemo) return;
    e.stopPropagation();
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  const handleItemDragOver = (e: React.DragEvent, index: number) => {
    if (!canEditMemo) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (draggedItemIndex === null || draggedItemIndex === index) return;

    // Reorder items while dragging for visual feedback
    const items = [...expandedChecklistItems];
    const draggedItem = items[draggedItemIndex];
    items.splice(draggedItemIndex, 1);
    items.splice(index, 0, draggedItem);

    setExpandedChecklistItems(items);
    setDraggedItemIndex(index);
  };

  const handleItemDragEnd = (e: React.DragEvent) => {
    if (!canEditMemo) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggedItemIndex(null);
    // Autosave the reordered items
    autoSaveChecklistChanges(expandedChecklistItems);
  };

  const handleItemDrop = (e: React.DragEvent) => {
    if (!canEditMemo) return;
    e.preventDefault();
    e.stopPropagation();
    setDraggedItemIndex(null);
  };

  // Calculate checklist progress
  const getChecklistProgress = () => {
    if (!expandedChecklistItems || expandedChecklistItems.length === 0) return 0;
    const completed = expandedChecklistItems.filter(item => item.completed).length;
    return Math.round((completed / expandedChecklistItems.length) * 100);
  };

  // Handle drag start
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!canEditMemo) return;
    // Don't allow dragging if we're editing or deleting
    if (isEditing || isDeleting) return;

    // Don't allow internal dragging in grid or list view mode
    if (viewMode === 'grid' || viewMode === 'list') return;

    // Only start dragging from the drag handle
    if (!(e.target as HTMLElement).closest('.drag-handle')) return;

    e.preventDefault();
    e.stopPropagation();

    // Get the starting mouse position
    const startX = e.clientX;
    const startY = e.clientY;

    // Store the initial card position
    const initialLeft = memo.positionX;
    const initialTop = memo.positionY;

    setIsDragging(true);

    // Track mouse movement
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !cardRef.current) return;

      e.preventDefault();

      // Calculate how far we've moved from the starting position in screen pixels
      const screenDeltaX = e.clientX - startX;
      const screenDeltaY = e.clientY - startY;

      // Convert screen pixels to board space
      const deltaX = screenDeltaX / boardScale;
      const deltaY = screenDeltaY / boardScale;

      // Apply the transform directly to the card element for smooth dragging
      // We use translate3d for better performance
      cardRef.current.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;

    };

    // Handle drag end
    const handleMouseUp = (e: MouseEvent) => {
      if (!cardRef.current) return;

      e.preventDefault();

      // Remove event listeners first
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);

      // Calculate the final position in screen pixels
      const screenDeltaX = e.clientX - startX;
      const screenDeltaY = e.clientY - startY;

      // Convert screen pixels to board space
      const deltaX = screenDeltaX / boardScale;
      const deltaY = screenDeltaY / boardScale;

      // Reset transform immediately to avoid the jump
      cardRef.current.style.transform = 'none';

      setIsDragging(false);

      // Only update position if we actually moved
      if (Math.abs(screenDeltaX) > 2 || Math.abs(screenDeltaY) > 2) {
        onPositionChange(
          memo.id,
          initialLeft + deltaX,
          initialTop + deltaY
        );
      }
    };

    // Add event listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

  }, [isEditing, isDeleting, memo.id, memo.positionX, memo.positionY, boardScale, onPositionChange, canEditMemo]);

  // Format date
  const formatDate = (dateString: string | undefined) => {
    // Return placeholder if date is invalid or undefined
    if (!dateString) {
      console.warn('formatDate: No date string provided');
      return 'N/A';
    }

    try {
      // Handle various date formats
      let date: Date;

      // Try parsing as ISO string first
      date = new Date(dateString);

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.error('Invalid date format:', dateString);
        return 'Invalid Date';
      }

      // Format the date
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Error';
    }
  };

  return (
    <>
      <div
        ref={cardRef}
        className={`memo-card-hover relative p-4 rounded-xl shadow-md transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-0.5 cursor-pointer border border-black/5 overflow-hidden flex flex-col ${getColorStyle()}`}
        style={viewMode === 'grid' ? {
          minHeight: '120px',
          maxHeight: '600px',
          width: '100%',
          userSelect: 'none'
        } : {
          width: '100%',
          minHeight: '120px',
          userSelect: 'none'
        }}
        onClick={(e) => {
          // Only expand if not clicking on buttons or in edit mode
          if (!isEditing && !isDeleting && !(e.target as HTMLElement).closest('button')) {
            // Reset expanded state to current memo values
            setExpandedTitle(memo.title);
            setExpandedContent(memo.content);
            setExpandedChecklistItems(memo.checklistItems || []);
            setExpandedColor(memo.color);
            setIsExpanded(true);
          }
        }}
      >

        {/* Drag handle */}
        <div
          className="drag-handle absolute top-1 right-1 p-1.5 cursor-move rounded-lg hover:bg-black/5 transition-colors"
          onMouseDown={handleDragStart}
        >
          <GripVertical size={16} className="opacity-40 hover:opacity-80 transition-opacity" />
        </div>

        {/* Sharing indicator */}
        {memo.accessLevel === 'view' || memo.accessLevel === 'edit' ? (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs bg-blue-500/90 text-white flex items-center space-x-1 shadow-sm">
            <Users size={10} />
            <span>Shared by {memo.ownerName}</span>
          </div>
        ) : null}

        {/* Card content */}
        <div className="p-4 pt-5 flex-1 flex flex-col min-h-0 overflow-y-auto custom-scrollbar hide-scrollbar">
          {isEditing ? (
            <div className="space-y-3">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full p-2.5 rounded-lg border-2 font-medium transition-all ${theme === 'dark' ? 'bg-gray-700/50 text-white border-gray-600 focus:border-blue-500' : 'bg-white/80 text-gray-800 border-gray-300 focus:border-blue-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                placeholder={t('memo.title')}
              />
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className={`w-full p-2.5 rounded-lg border-2 resize-none transition-all ${theme === 'dark' ? 'bg-gray-700/50 text-white border-gray-600 focus:border-blue-500' : 'bg-white/80 text-gray-800 border-gray-300 focus:border-blue-400'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                rows={5}
                placeholder={t('memo.content')}
              />

              {/* Color selection */}
              <div className="mt-3">
                <label className={`block mb-2 text-xs font-semibold uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('memo.color')}
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {availableColors.map((colorOption) => (
                    <button
                      key={colorOption}
                      type="button"
                      onClick={() => setSelectedColor(colorOption)}
                      className={`w-6 h-6 rounded-lg transition-all hover:scale-110 ${theme === 'dark'
                        ? colorStyles[colorOption].darkBg
                        : colorStyles[colorOption].bg
                        } ${selectedColor === colorOption ? 'ring-2 ring-offset-2 ring-blue-500 scale-105' : 'hover:ring-2 hover:ring-offset-1 hover:ring-gray-400'
                        }`}
                      title={colorOption.charAt(0).toUpperCase() + colorOption.slice(1)}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setIsEditing(false)}
                  className={`p-2 rounded-lg transition-all hover:scale-105 shadow-sm ${theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                >
                  <X size={16} />
                </button>
                <button
                  onClick={handleSave}
                  className={`p-2 rounded-lg transition-all hover:scale-105 shadow-sm ${theme === 'dark'
                    ? 'bg-green-600 hover:bg-green-500 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                    }`}
                >
                  <Check size={16} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="font-bold text-lg mb-3 break-words leading-tight line-clamp-2 flex-shrink-0">
                {memo.title || <span className={`italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Untitled memo</span>}
              </h3>

              {/* Display checklist items if type is checklist, otherwise show content */}
              {memoType === 'checklist' ? (
                <div className="space-y-1.5 flex-1 min-h-0 overflow-y-auto custom-scrollbar hide-scrollbar mb-10" style={{ maxHeight: '400px' }}>
                  {memo.checklistItems && memo.checklistItems.length > 0 ? (
                    memo.checklistItems.map((item, index) => (
                    <div key={item.id || index} className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={!canEditMemo}
                        onClick={(e) => {
                          if (!canEditMemo) return;
                          e.stopPropagation();
                          handleToggleChecklistItem(item.id);
                        }}
                        className={`flex-shrink-0 transition-all hover:scale-110 ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
                          } ${!canEditMemo ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`}
                        aria-disabled={!canEditMemo}
                      >
                        {item.completed ? (
                          <CheckSquare size={17} className="text-green-500" />
                        ) : (
                          <Square size={17} />
                        )}
                      </button>
                      <span
                        className={`flex-1 text-sm leading-snug transition-all ${item.completed
                          ? 'line-through opacity-50'
                          : ''
                          }`}
                      >
                        {item.text}
                      </span>
                    </div>
                  ))
                  ) : (
                    <div className={`text-center py-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      <ListTodo size={32} className="mx-auto mb-2 opacity-40" />
                      <p className="text-sm mb-1">No tasks yet</p>
                      <p className="text-xs opacity-60">Click to add checklist items</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="whitespace-pre-wrap break-words text-sm leading-relaxed flex-1 min-h-0 overflow-y-auto custom-scrollbar hide-scrollbar mb-10" style={{ maxHeight: '400px' }}>
                  {memo.content}
                </p>
              )}
              <div className="mt-auto pt-2 border-t border-opacity-20 text-xs opacity-60 font-medium flex-shrink-0">
                Last Edit: {formatDate(memo.updatedAt)}
              </div>
              <div className="absolute bottom-2 right-2 flex gap-1.5 opacity-0 memo-card-hover:hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Reset expanded state to current memo values
                    setExpandedTitle(memo.title);
                    setExpandedContent(memo.content);
                    setExpandedChecklistItems(memo.checklistItems || []);
                    setExpandedColor(memo.color);
                    setIsExpanded(true);
                  }}
                  className={`p-1.5 rounded-lg transition-all hover:scale-110 shadow-sm backdrop-blur-sm ${theme === 'dark'
                    ? 'bg-gray-700/90 hover:bg-gray-600 text-gray-300'
                    : 'bg-white/90 hover:bg-gray-100 text-gray-700'
                    }`}
                  title="Expand memo"
                >
                  <Maximize2 size={14} />
                </button>

                {/* Share button - only for owners */}
                {memo.accessLevel === 'owner' && onShare && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onShare(memo);
                    }}
                    className={`p-1.5 rounded-lg transition-all hover:scale-110 shadow-sm backdrop-blur-sm ${theme === 'dark'
                        ? 'bg-gray-700/90 hover:bg-blue-600 text-gray-300 hover:text-white'
                        : 'bg-white/90 hover:bg-blue-500 text-gray-700 hover:text-white'
                      }`}
                    title="Share memo"
                  >
                    <Share2 size={14} />
                  </button>
                )}

                {/* Delete button - only for owners */}
                {memo.accessLevel === 'owner' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDeleting(true);
                    }}
                    className={`p-1.5 rounded-lg transition-all hover:scale-110 shadow-sm backdrop-blur-sm ${theme === 'dark'
                      ? 'bg-gray-700/90 hover:bg-red-600 text-gray-300 hover:text-white'
                      : 'bg-white/90 hover:bg-red-500 text-gray-700 hover:text-white'
                      }`}
                    title="Delete memo"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Expanded Modal View - Enhanced for Checklist Editing */}
      {isExpanded && createPortal(
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200 cursor-default"
          style={{ margin: 0 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCancelExpanded();
            }
          }}
        >
          <div
            className={`w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl shadow-2xl border animate-in zoom-in-95 duration-300 cursor-default ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Clean Header - Google Keep Style */}
            <div className={`px-6 py-5 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="flex items-start justify-between gap-4">
                {/* Title Input */}
                <input
                  type="text"
                  value={expandedTitle}
                  onChange={(e) => setExpandedTitle(e.target.value)}
                  className={`text-xl font-semibold bg-transparent outline-none flex-1 min-w-0 transition-all
                    ${theme === 'dark'
                      ? 'text-white placeholder-gray-500 focus:placeholder-gray-600'
                      : 'text-gray-900 placeholder-gray-400 focus:placeholder-gray-500'
                    }
                    ${!canEditMemo ? 'cursor-not-allowed opacity-80' : ''}`}
                  readOnly={!canEditMemo}
                  disabled={!canEditMemo}
                  placeholder="Title"
                />

                {/* Close Button */}
                <button
                  onClick={handleCancelExpanded}
                  className={`p-2 rounded-full transition-all hover:scale-105 flex-shrink-0 ${theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-400 hover:text-white'
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    }`}
                  title="Close"
                >
                  <X size={20} />
                </button>
              </div>

              {isViewOnly && (
                <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${theme === 'dark' ? 'bg-amber-500/20 text-amber-200 border border-amber-400/40' : 'bg-amber-50 text-amber-700 border border-amber-300'}`}>
                  <Users size={12} />
                  <span>View Only Access</span>
                </div>
              )}
            </div>

            {/* Content Area - Scrollable */}
            <div
              className={`overflow-y-auto px-6 py-6 custom-scrollbar ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}
              style={{ maxHeight: 'calc(90vh - 200px)' }}
            >
              {/* Checklist Section */}
              {memo.type === 'checklist' ? (
                <div className="mb-8">
                  {expandedChecklistItems && expandedChecklistItems.length > 0 ? (
                    <>
                      {/* Progress Bar */}
                      <div className="mb-5">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`text-sm font-semibold uppercase tracking-wide ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                            Checklist Items
                          </h3>
                          <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                            {expandedChecklistItems.filter(i => i.completed).length} / {expandedChecklistItems.length} completed
                          </span>
                        </div>
                        <div className={`w-full h-2 rounded-full overflow-hidden ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                          }`}>
                          <div
                            className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500 ease-out"
                            style={{ width: `${getChecklistProgress()}%` }}
                          />
                        </div>
                      </div>

                      {/* Checklist Items */}
                      <div
                        ref={checklistContainerRef}
                        className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar scroll-smooth mb-4"
                      >
                        {expandedChecklistItems.map((item, index) => (
                      <div
                        key={item.id || index}
                        draggable={canEditMemo}
                        onDragStart={(e) => handleItemDragStart(e, index)}
                        onDragOver={(e) => handleItemDragOver(e, index)}
                        onDragEnd={handleItemDragEnd}
                        onDrop={handleItemDrop}
                        className={`group flex items-start gap-3 p-3 rounded-lg transition-all cursor-default ${draggedItemIndex === index ? 'opacity-50' : 'opacity-100'
                          } ${theme === 'dark'
                            ? 'hover:bg-gray-700/50 border border-transparent hover:border-gray-600'
                            : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                          }`}
                      >
                        {/* Drag Handle */}
                        <div className={`flex-shrink-0 mt-1 cursor-move opacity-40 hover:opacity-100 transition-opacity ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                          <GripVertical size={18} />
                        </div>

                        {/* Checkbox */}
                        <button
                          type="button"
                          disabled={!canEditMemo}
                          onClick={() => {
                            if (!canEditMemo) return;
                            const updatedItems = expandedChecklistItems.map(i =>
                              i.id === item.id ? { ...i, completed: !i.completed } : i
                            );
                            setExpandedChecklistItems(updatedItems);
                            // Autosave the completion toggle
                            autoSaveChecklistChanges(updatedItems);
                          }}
                          className={`flex-shrink-0 mt-1 transition-all hover:scale-110 ${theme === 'dark' ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'
                            } ${!canEditMemo ? 'opacity-50 cursor-not-allowed hover:scale-100' : ''}`}
                        >
                          {item.completed ? (
                            <CheckSquare size={22} className="text-green-500" />
                          ) : (
                            <Square size={22} />
                          )}
                        </button>

                        {/* Item Number */}
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                          }`}>
                          {index + 1}
                        </span>

                        {/* Text Input */}
                        <input
                          type="text"
                          value={item.text}
                          onChange={(e) => {
                            if (!canEditMemo) return;
                            const updatedItems = expandedChecklistItems.map(i =>
                              i.id === item.id ? { ...i, text: e.target.value } : i
                            );
                            setExpandedChecklistItems(updatedItems);
                            // Debounced autosave for text changes
                            debouncedAutoSave(updatedItems);
                          }}
                          className={`flex-1 bg-transparent border-none outline-none text-base leading-relaxed font-medium transition-all ${item.completed ? 'line-through opacity-50' : ''
                            } ${theme === 'dark' ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                            } ${!canEditMemo ? 'cursor-not-allowed' : ''}`}
                          placeholder="Enter task description..."
                          readOnly={!canEditMemo}
                          disabled={!canEditMemo}
                        />

                        {/* Delete Button */}
                        {canEditMemo && (
                          <button
                            onClick={() => handleDeleteChecklistItem(item.id)}
                            className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-all p-1.5 rounded-lg ${theme === 'dark'
                              ? 'hover:bg-red-600/20 text-gray-400 hover:text-red-400'
                              : 'hover:bg-red-50 text-gray-400 hover:text-red-600'
                              }`}
                            title="Delete item"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    // Empty state for checklist with no items
                    <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      <ListTodo size={48} className="mx-auto mb-3 opacity-30" />
                      <h4 className="text-lg font-semibold mb-2">Empty Checklist</h4>
                      <p className="text-sm opacity-70 mb-4">Add tasks to get started with your checklist</p>
                    </div>
                  )}

                  {/* Add New Item */}
                  <div className={`flex items-center gap-3 p-3 rounded-lg border-2 border-dashed transition-all ${theme === 'dark'
                    ? 'border-gray-600 hover:border-blue-500 bg-gray-700/30'
                    : 'border-gray-300 hover:border-blue-400 bg-gray-50/50'
                    } ${!canEditMemo ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    <Plus className={`flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`} size={22} />
                    <input
                      ref={newItemInputRef}
                      type="text"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyDown={handleNewItemKeyDown}
                      className={`flex-1 bg-transparent border-none outline-none text-base ${theme === 'dark' ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                        } ${!canEditMemo ? 'cursor-not-allowed' : ''}`}
                      readOnly={!canEditMemo}
                      disabled={!canEditMemo}
                      placeholder="Add new task... (Press Enter)"
                    />
                    {newChecklistItem.trim() && canEditMemo && (
                      <button
                        onClick={handleAddChecklistItem}
                        className={`flex-shrink-0 px-3 py-1.5 rounded-lg font-medium text-sm transition-all hover:scale-105 ${theme === 'dark'
                          ? 'bg-blue-600 hover:bg-blue-500 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                          }`}
                      >
                        Add
                      </button>
                    )}
                  </div>
                  {!canEditMemo && (
                    <p className={`text-xs mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      You have view-only access to this memo.
                    </p>
                  )}
                </div>
              ) : (
                /* Content Section for non-checklist memos */
                <div className="mb-8">
                  <h3 className={`text-sm font-semibold uppercase tracking-wide mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                    Content
                  </h3>
                  <textarea
                    value={expandedContent}
                    onChange={(e) => setExpandedContent(e.target.value)}
                    className={`w-full text-base leading-relaxed bg-transparent border-2 rounded-lg p-4 outline-none resize-none min-h-[200px] transition-all ${theme === 'dark'
                      ? 'text-white placeholder-gray-500 border-gray-600 focus:border-blue-500'
                      : 'text-gray-900 placeholder-gray-400 border-gray-300 focus:border-blue-400'
                      } focus:ring-2 focus:ring-blue-500/20 ${!canEditMemo ? 'opacity-75 cursor-not-allowed' : ''}`}
                    readOnly={!canEditMemo}
                    disabled={!canEditMemo}
                    placeholder="Write your notes here..."
                  />
                </div>
              )}

              {/* Metadata */}
              <div className={`mt-6 pt-4 border-t text-sm ${theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'
                }`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-4">
                    <span>Created: {formatDate(memo.createdAt) || 'N/A'}</span>
                    <span>•</span>
                    <span>Updated: {formatDate(memo.updatedAt) || 'N/A'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Toolbar - Google Keep Style */}
            <div className={`${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              {/* Color Picker - Inline Expandable */}
              {showColorPicker && canEditMemo && (
                <div className={`px-4 py-3 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="flex flex-wrap gap-3 justify-start">
                    {availableColors.map((colorOption) => (
                      <button
                        key={colorOption}
                        type="button"
                        onClick={() => {
                          setExpandedColor(colorOption);
                          setShowColorPicker(false);
                        }}
                        className={`w-9 h-9 rounded-full transition-all hover:scale-110 relative flex-shrink-0 ${
                          theme === 'dark'
                            ? colorStyles[colorOption].darkBg
                            : colorStyles[colorOption].bg
                        } ${
                          expandedColor === colorOption
                            ? 'ring-2 ring-blue-500'
                            : 'hover:ring-2 hover:ring-gray-400'
                        }`}
                        title={colorOption.charAt(0).toUpperCase() + colorOption.slice(1)}
                      >
                        {expandedColor === colorOption && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Check size={16} className={`${theme === 'dark' ? 'text-white' : 'text-gray-800'}`} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Main Toolbar */}
              <div className="px-4 py-3 flex items-center justify-between">
                {/* Left: Action Buttons */}
                <div className="flex items-center gap-1">
                  {/* Color Picker Button */}
                  {canEditMemo && (
                    <button
                      onClick={() => setShowColorPicker(!showColorPicker)}
                      className={`p-2 rounded-full transition-all ${
                        showColorPicker
                          ? theme === 'dark' ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-900'
                          : theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                      }`}
                      title="Background options"
                    >
                      <Palette size={18} />
                    </button>
                  )}

                  {/* Type Toggle Buttons */}
                  {canEditMemo && (
                    <>
                      <button
                        onClick={() => handleConvertType('note')}
                        disabled={memoType === 'note'}
                        className={`p-2 rounded-full transition-all ${
                          memoType === 'note'
                            ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                            : theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                        } ${memoType === 'note' ? '' : 'hover:scale-105'}`}
                        title="Convert to note"
                      >
                        <FileText size={18} />
                      </button>
                      <button
                        onClick={() => handleConvertType('checklist')}
                        disabled={memoType === 'checklist'}
                        className={`p-2 rounded-full transition-all ${
                          memoType === 'checklist'
                            ? theme === 'dark' ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
                            : theme === 'dark' ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                        } ${memoType === 'checklist' ? '' : 'hover:scale-105'}`}
                        title="Convert to checklist"
                      >
                        <ListTodo size={18} />
                      </button>
                    </>
                  )}

                  {/* Delete Button */}
                  {isOwner && (
                    <button
                      onClick={() => {
                        handleCancelExpanded();
                        setIsDeleting(true);
                      }}
                      className={`p-2 rounded-full transition-all hover:scale-105 ${
                        theme === 'dark'
                          ? 'hover:bg-red-600/20 text-gray-400 hover:text-red-400'
                          : 'hover:bg-red-50 text-gray-600 hover:text-red-600'
                      }`}
                      title="Delete memo"
                    >
                      <Trash2 size={18} />
                    </button>
                  )}
                </div>

                {/* Right: Close Button */}
                <button
                  onClick={handleCancelExpanded}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-300 hover:text-white'
                      : 'hover:bg-gray-100 text-gray-700 hover:text-gray-900'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Modal */}
      {isDeleting && createPortal(
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 animate-in fade-in duration-200"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsDeleting(false);
            }
          }}
        >
          <div
            className={`w-full max-w-md rounded-2xl shadow-2xl border animate-in zoom-in-95 duration-300 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`p-6 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
              <h2 className="text-2xl font-bold text-red-600 flex items-center gap-2">
                <Trash2 size={24} />
                Confirm Delete
              </h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className={`text-base leading-relaxed ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                Are you sure you want to delete this memo? This action cannot be undone.
              </p>

              {/* Memo preview */}
              <div className={`mt-5 p-4 rounded-lg border-2 ${theme === 'dark' ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                }`}>
                <h4 className={`font-semibold text-xs uppercase tracking-wide mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>Memo to delete:</h4>
                <p className="font-bold text-lg truncate mb-1">{memo.title}</p>
                {memo.content && (
                  <p className={`text-sm mt-2 line-clamp-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>{memo.content}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className={`flex justify-end gap-3 p-6 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
              <button
                onClick={() => setIsDeleting(false)}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all hover:scale-105 shadow-sm ${theme === 'dark'
                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    await onDelete(memo.id);
                    setIsDeleting(false);
                  } catch (error) {
                    console.error('Failed to delete memo:', error);
                  }
                }}
                className={`px-6 py-2.5 rounded-lg font-medium transition-all hover:scale-105 shadow-sm ${theme === 'dark'
                  ? 'bg-red-600 hover:bg-red-500 text-white'
                  : 'bg-red-500 hover:bg-red-600 text-white'
                  }`}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
